import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatHours,
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    formatUtcLocalShort,
    toNum,
} from "../../bids/analytics/format";
import { UeFilters, UeFiltersState } from "../components/UeFilters";
import { TermHint, TermHintsRow } from "../components/TermHint";
import {
    ueActivationFunnelApi,
    ueLtvApi,
    ueParetoApi,
    ueRepeatRateApi,
    ueRetentionApi,
    ueTimeToValueApi,
} from "../unitEconomicsApi";
import Link from "@mui/joy/Link";

type Props = {
    isActive: boolean;
    filters: UeFiltersState;
    onFiltersChange: (next: UeFiltersState) => void;
};

export function ActivationFunnelTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueActivationFunnelApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;
    const chart = React.useMemo(() => {
        if (!d) return [];
        const map = new Map<number, any>();
        const add = (points: typeof d.registeredSeries, key: string) => {
            for (const p of points ?? []) {
                const t = new Date(p.bucketUtc).getTime();
                const row = map.get(t) ?? { x: new Date(p.bucketUtc) };
                row[key] = toNum(p.count);
                map.set(t, row);
            }
        };
        add(d.registeredSeries, "registered");
        add(d.depositedSeries, "deposited");
        add(d.tradedSeries, "traded");
        add(d.soldSeries, "sold");
        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
    }, [d]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="funnel">Воронка</TermHint> активации
                        </>
                    }
                    subtitle="Среди пользователей, зарегистрированных в периоде: кто дошёл до депозита, первой покупки и первой продажи."
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Регистрации</strong> — новые пользователи за период
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Депозит</strong> — первый <TermHint term="paid" /> депозит после
                                    регистрации (до конца периода)
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Покупка</strong> — первый завершённый ордер как{" "}
                                    <TermHint term="buyer">покупатель</TermHint>;{" "}
                                    <strong>продажа</strong> — как{" "}
                                    <TermHint term="seller">продавец</TermHint>
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: "Конверсия в депозит",
                                    ifGrows: "онбординг и платёжный UX работают лучше.",
                                    ifFalls: "узкое место на первом пополнении — смотрите методы и трение.",
                                },
                                {
                                    signal: "Конверсия регистрация → покупка",
                                    ifGrows: "новички быстрее начинают покупать.",
                                    ifFalls: "теряете покупателей до первой сделки.",
                                },
                                {
                                    signal: "Конверсия регистрация → продажа",
                                    ifGrows: "больше новичков выходят на сторону предложения.",
                                    ifFalls: "приток продавцов слабеет относительно регистраций.",
                                },
                            ]}
                        />
                    }
                >
                    {d.registered === 0 ? (
                        <EmptyState hint="Нет регистраций за период." />
                    ) : (
                        <>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                <MetricHint label="Регистрации" value={formatInt(d.registered)} color="primary" />
                                <MetricHint
                                    label="С депозитом"
                                    value={formatInt(d.deposited)}
                                    hint={formatRatePercent(d.depositRate)}
                                    color="success"
                                />
                                <MetricHint
                                    label="С покупкой"
                                    value={formatInt(d.traded)}
                                    hint={formatRatePercent(d.tradeRate)}
                                />
                                <MetricHint
                                    label="С продажей"
                                    value={formatInt(d.sold)}
                                    hint={formatRatePercent(d.sellRate)}
                                />
                                <MetricHint
                                    label="Депозит → покупка"
                                    value={
                                        d.deposited > 0 ? formatRatePercent(d.depositToTradeRate) : "—"
                                    }
                                />
                            </Box>
                            {chart.length > 0 && (
                                <LineChart
                                    height={260}
                                    dataset={chart}
                                    xAxis={[{ dataKey: "x", scaleType: "time" }]}
                                    series={[
                                        { dataKey: "registered", label: "Регистрации", color: "#1976d2" },
                                        { dataKey: "deposited", label: "Депозит", color: "#2e7d32" },
                                        { dataKey: "traded", label: "Покупка", color: "#ed6c02" },
                                        { dataKey: "sold", label: "Продажа", color: "#6a1b9a" },
                                    ]}
                                    margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                                />
                            )}
                        </>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function TimeToValueTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueTimeToValueApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            Скорость до ценности (<TermHintsRow terms={["ttfd", "ttft"]} />)
                        </>
                    }
                    subtitle="Сколько часов от регистрации до первого депозита и первой сделки."
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="ttfd" /> — время до первого депозита;{" "}
                                    <TermHint term="ttft" /> — время до первой сделки
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="median" /> — типичный путь; <TermHint term="p90" /> — «хвост»
                                    медленных пользователей
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            <TermHint term="median" /> / <TermHint term="p90" /> часов
                                        </>
                                    ),
                                    growsIsGood: false,
                                    ifGrows: "путь до ценности удлиняется — хуже удержание.",
                                    ifFalls: "пользователи быстрее получают ценность — хорошо для LTV.",
                                },
                            ]}
                        />
                    }
                >
                    {d.usersWithDeposit === 0 && d.usersWithTrade === 0 ? (
                        <EmptyState />
                    ) : (
                        <>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                <MetricHint
                                    label={
                                        <>
                                            <TermHint term="median" /> до депозита
                                        </>
                                    }
                                    value={formatHours(d.medianHoursToDeposit, d.usersWithDeposit)}
                                    color="success"
                                />
                                <MetricHint
                                    label={
                                        <>
                                            <TermHint term="p90" /> до депозита
                                        </>
                                    }
                                    value={formatHours(d.p90HoursToDeposit, d.usersWithDeposit)}
                                />
                                <MetricHint
                                    label={
                                        <>
                                            <TermHint term="median" /> до сделки
                                        </>
                                    }
                                    value={formatHours(d.medianHoursToTrade, d.usersWithTrade)}
                                    color="primary"
                                />
                                <MetricHint
                                    label={
                                        <>
                                            <TermHint term="p90" /> до сделки
                                        </>
                                    }
                                    value={formatHours(d.p90HoursToTrade, d.usersWithTrade)}
                                />
                            </Box>
                            <Typography level="title-sm">Распределение до депозита</Typography>
                            <BarChart
                                height={220}
                                xAxis={[{ data: d.depositHistogram.map((b) => b.label), scaleType: "band" }]}
                                series={[{ data: d.depositHistogram.map((b) => b.count), label: "Пользователи", color: "#2e7d32" }]}
                                margin={{ left: 40, right: 20, top: 20, bottom: 40 }}
                            />
                            <Typography level="title-sm">Распределение до сделки</Typography>
                            <BarChart
                                height={220}
                                xAxis={[{ data: d.tradeHistogram.map((b) => b.label), scaleType: "band" }]}
                                series={[{ data: d.tradeHistogram.map((b) => b.count), label: "Пользователи", color: "#1976d2" }]}
                                margin={{ left: 40, right: 20, top: 20, bottom: 40 }}
                            />
                        </>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function RetentionTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueRetentionApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="retention" /> и <TermHint term="churn" />
                        </>
                    }
                    subtitle={
                        <>
                            <TermHint term="cohort">Когорты</TermHint> по месяцу регистрации: доля с
                            активностью на <TermHintsRow terms={["d1", "d7", "d30"]} />.{" "}
                            <TermHint term="churn" /> — нет активности 30 дней.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHintsRow terms={["d1", "d7", "d30"]} /> — доля когорты, у кого
                                    LastActivityAt ≥ Created + N дней
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="churn" /> rate — доля пользователей без активности за
                                    последние 30 дней
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHintsRow terms={["d1", "d7", "d30"]} />,
                                    ifGrows: "когорты возвращаются чаще — растёт потенциал LTV.",
                                    ifFalls: "удержание слабеет; проверьте продукт и реактивацию.",
                                },
                                {
                                    signal: <TermHint term="churn" />,
                                    growsIsGood: false,
                                    ifGrows: "больше «остывших» аккаунтов — риск для юнит-экономики.",
                                    ifFalls: "база активнее — здоровый сигнал.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint
                            label={
                                <>
                                    Средний <TermHint term="d1" />
                                </>
                            }
                            value={formatRatePercent(d.avgD1)}
                            color="success"
                        />
                        <MetricHint
                            label={
                                <>
                                    Средний <TermHint term="d7" />
                                </>
                            }
                            value={formatRatePercent(d.avgD7)}
                        />
                        <MetricHint
                            label={
                                <>
                                    Средний <TermHint term="d30" />
                                </>
                            }
                            value={formatRatePercent(d.avgD30)}
                            color="primary"
                        />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="churn" /> (30д)
                                </>
                            }
                            value={formatRatePercent(d.churnRate)}
                            hint={`${formatInt(d.churnedUsers)} из базы; акт. ${formatInt(d.activeUsers)}`}
                            color="danger"
                        />
                    </Box>
                    {d.cohorts.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <ScrollTable>
                            <Table size="sm" hoverRow>
                                <thead>
                                    <tr>
                                        <th>
                                            <TermHint term="cohort" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>Рег.</th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="d1" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="d7" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="d30" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.cohorts.map((c) => (
                                        <tr key={c.cohortMonth}>
                                            <td>{formatUtcLocalShort(c.cohortMonth).slice(0, 7)}</td>
                                            <td style={{ textAlign: "right" }}>{formatInt(c.registered)}</td>
                                            <td style={{ textAlign: "right" }}>{formatRatePercent(c.d1)}</td>
                                            <td style={{ textAlign: "right" }}>{formatRatePercent(c.d7)}</td>
                                            <td style={{ textAlign: "right" }}>{formatRatePercent(c.d30)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

function ContributionTable(users: { userId: any; userName: string; tradeProfit: number; depositProfit: number; withdrawalProfit: number; totalProfit: number; orderCount: number }[]) {
    return (
        <ScrollTable>
            <Table size="sm" hoverRow>
                <thead>
                    <tr>
                        <th>Пользователь</th>
                        <th style={{ textAlign: "right" }}>Ордера</th>
                        <th style={{ textAlign: "right" }}>
                            Trade <TermHint term="fee" />
                        </th>
                        <th style={{ textAlign: "right" }}>Депозиты</th>
                        <th style={{ textAlign: "right" }}>Выводы</th>
                        <th style={{ textAlign: "right" }}>Итого</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((u) => (
                        <tr key={String(u.userId)}>
                            <td>
                                <Link
                                    href={`/app/users/${u.userId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {u.userName || String(u.userId)}
                                </Link>
                            </td>
                            <td style={{ textAlign: "right" }}>{formatInt(u.orderCount)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.tradeProfit)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.depositProfit)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.withdrawalProfit)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.totalProfit)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}

export function LtvTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueLtvApi({ from: filters.from, to: filters.to, take: filters.take }),
        isActive,
        [filters.from, filters.to, filters.take]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                showTake
            />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="ltv" /> (<TermHint term="contribution" />)
                        </>
                    }
                    subtitle={
                        <>
                            Сумма trade <TermHint term="fee" /> (как покупатель) +{" "}
                            <TermHint term="ourBenefit" /> депозитов/выводов на пользователя за период.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Это period <TermHint term="contribution" />, не lifetime за всю жизнь
                                    аккаунта
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    Средний и медианный <TermHint term="ltv" /> — по пользователям с
                                    ненулевым contribution
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            Средний / медианный <TermHint term="ltv" />
                                        </>
                                    ),
                                    ifGrows: "пользователи приносят больше маржи — можно агрессивнее удерживать.",
                                    ifFalls: "ценность когорты падает; смотрите take rate и активность.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint
                            label={
                                <>
                                    Средний <TermHint term="ltv" />
                                </>
                            }
                            value={formatMoneyKopecks(d.averageLtv)}
                            color="success"
                        />
                        <MetricHint
                            label={
                                <>
                                    Медианный <TermHint term="ltv" />
                                </>
                            }
                            value={formatMoneyKopecks(d.medianLtv)}
                        />
                        <MetricHint label="Пользователей" value={formatInt(d.usersWithContribution)} />
                        <MetricHint
                            label={
                                <>
                                    Сумма <TermHint term="contribution" />
                                </>
                            }
                            value={formatMoneyKopecks(d.totalContribution)}
                            color="primary"
                        />
                    </Box>
                    {d.topUsers.length === 0 ? <EmptyState /> : ContributionTable(d.topUsers)}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function RepeatRateTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueRepeatRateApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={<TermHint term="repeat">Повторные действия</TermHint>}
                    subtitle="Доля пользователей с ≥2 депозитами / ≥2 ордерами за период."
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Отделяет one-shot пользователей от recurring ядра (
                                    <TermHint term="repeat" />)
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHint term="repeat" />,
                                    ifGrows: "больше recurring — здоровее юнит-экономика.",
                                    ifFalls: "растёт доля one-shot; слабее LTV.",
                                },
                                {
                                    signal: "Ордеров на покупателя",
                                    ifGrows: "частота торговли растёт.",
                                    ifFalls: "покупатели реже возвращаются к сделкам.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="repeat" /> депозиты
                                </>
                            }
                            value={formatRatePercent(d.repeatDepositRate)}
                            hint={`${formatInt(d.usersWithRepeatDeposit)} / ${formatInt(d.usersWithDeposit)}`}
                            color="success"
                        />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="repeat" /> ордера
                                </>
                            }
                            value={formatRatePercent(d.repeatOrderRate)}
                            hint={`${formatInt(d.usersWithRepeatOrder)} / ${formatInt(d.usersWithOrder)}`}
                            color="primary"
                        />
                        <MetricHint
                            label="Ордеров / покупатель"
                            value={toNum(d.avgOrdersPerActiveBuyer).toFixed(2)}
                        />
                    </Box>
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function ParetoTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueParetoApi({ from: filters.from, to: filters.to, take: filters.take }),
        isActive,
        [filters.from, filters.to, filters.take]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                showTake
            />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            Концентрация прибыли (<TermHint term="pareto" />)
                        </>
                    }
                    subtitle={
                        <>
                            Какую долю положительного <TermHint term="contribution" /> дают top 1% / 5% /
                            10% пользователей.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Высокая концентрация = зависимость от китов (
                                    <TermHint term="pareto" />)
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: "Доля top 1% / 5% / 10%",
                                    growsIsGood: false,
                                    ifGrows: "бизнес всё сильнее зависит от узкого круга — риск оттока китов.",
                                    ifFalls: "прибыль распределена шире — устойчивее база.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint label="Top 1%" value={formatRatePercent(d.top1Share)} color="danger" />
                        <MetricHint label="Top 5%" value={formatRatePercent(d.top5Share)} color="warning" />
                        <MetricHint label="Top 10%" value={formatRatePercent(d.top10Share)} />
                        <MetricHint label="Прибыль &gt;0" value={formatMoneyKopecks(d.totalProfit)} color="success" />
                    </Box>
                    {d.topUsers.length === 0 ? <EmptyState /> : ContributionTable(d.topUsers)}
                </AnalyticsCard>
            )}
        </Box>
    );
}
